import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { Grid } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import PageHeader from 'components/PageHeader';
import { Permission } from 'interfaces/permissionOption';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { isPfizerDemoLab } from 'utils/demo';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { usePermissions } from 'utils/usePermissions';

import { MainButton } from 'components/atoms/Buttons/MainButton';
import CopyableText from 'components/atoms/CopyableText';
import { studyDashboardHeaderHeight } from 'components/atoms/FlexContainers/FlexContainers';
import StudySettingsControl from 'components/StudiesDashboard/StudySettings/StudySettingsControl';
import { Study } from 'interfaces/study';
import { useCurrentUser } from 'utils/useCurrentUser';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import StudyManifestControl from './CaseManifest/CaseManifestControl';
import StudyUploadsStatus from './StudyUploadsStatus';

export const compactStudyDashboardHeaderHeight = 56;

interface Props {
  cohortMode: boolean;
  renderCohortDetailsToggle?: () => React.ReactNode;
  pageTitle: string;
  tab: string;
  compact?: boolean;

  tooltipText?: string;
  isAddSlidesDisabled?: boolean;
  study: Study;
  onAddSlidesClick?: () => void;
}

const StudyDashboardHeader = ({
  cohortMode,
  renderCohortDetailsToggle,
  pageTitle,
  tab,
  compact = false,
  tooltipText,
  isAddSlidesDisabled,
  study,
  onAddSlidesClick,
}: Props) => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const { queryParams } = useEncodedFilters();

  const { labId, stringifyWithLab } = useCurrentLabId();
  const pfizerDemoLab = isPfizerDemoLab(labId);

  const { userId: currentUserId } = useCurrentUser();

  const showStudySettingsControl =
    Boolean(study) &&
    (hasPermission(Permission.ViewStudySettings) || study.studyAdmins.includes(currentUserId)) &&
    !cohortMode;
  const showStudyManifestControl = hasPermission(Permission.ApplyCaseManifest) && !cohortMode && Boolean(study);
  const backButtonLabel = tab === 'cohorts' ? 'Back to cohorts' : 'Back to studies';
  const showCompactPrimaryActions = compact;

  const handleBackClick = () => {
    const search = stringifyWithLab({
      tab,
      resultsMode: queryParams.resultsMode,
    });
    navigate({
      pathname: `/`,
      search: `?${search}`,
    });
  };

  return (
    <Grid
      container
      py={compact ? 1 : 2}
      px={3}
      justifyContent="space-between"
      height={compact ? compactStudyDashboardHeaderHeight : studyDashboardHeaderHeight}
      alignItems="center"
      wrap="nowrap"
    >
      <Grid item container lg={7} md={6} xs={7} alignItems="center" spacing={2} wrap="nowrap" sx={{ minWidth: 0 }}>
        <PageHeader
          isCohort={cohortMode}
          renderCohortDetailsToggle={compact ? undefined : renderCohortDetailsToggle}
          pageTitle={pageTitle}
          pageSubtitle={
            !compact &&
            Boolean(study?.id) && (
              <CopyableText
                variant="caption"
                color="secondary"
                textForCopy={study.id}
                copyWithButton="right"
                copyButtonProps={{
                  'aria-label': 'Copy study ID',
                  size: 'small',
                  sx: { p: 0, ml: 0.5 },
                }}
                copyIconProps={{ sx: { fontSize: 14 } }}
                parentGridProps={{
                  width: 'fit-content',
                  justifyContent: 'flex-start',
                  sx: { maxWidth: '100%' },
                }}
                sx={{ display: 'block' }}
              >
                {study.id}
              </CopyableText>
            )
          }
          leadingAction={
            <Tooltip title={backButtonLabel} placement="top" arrow>
              <IconButton aria-label={backButtonLabel} onClick={handleBackClick} sx={{ mr: compact ? 1 : 2 }}>
                <ArrowBackRoundedIcon />
              </IconButton>
            </Tooltip>
          }
        />
      </Grid>

      <Grid item lg={5} md={6} xs={5} sx={{ minWidth: 0 }}>
        <Grid container direction="row" spacing={1} alignItems="center" justifyContent="end" wrap="nowrap">
          {!showCompactPrimaryActions && (
            <Grid item>
              <Grid container direction="row" spacing={1} alignItems="center" wrap="nowrap">
                <Grid item>
                  <StudyUploadsStatus navigateToUploadsDashboard />
                </Grid>
                <Grid item>
                  <StudyManifestControl studyId={study?.id} showStudyManifestControl={showStudyManifestControl} />
                </Grid>
              </Grid>
            </Grid>
          )}
          {!pfizerDemoLab && (
            <Grid item>
              <Tooltip title={tooltipText} placement="top" arrow>
                <span>
                  <MainButton
                    disableElevation
                    variant="contained"
                    color="secondary"
                    size={compact ? 'small' : 'medium'}
                    disabled={isAddSlidesDisabled}
                    onClick={onAddSlidesClick}
                    sx={compact ? { px: 1.5, minWidth: 'auto' } : undefined}
                  >
                    Add Slides
                  </MainButton>
                </span>
              </Tooltip>
            </Grid>
          )}
          {showStudySettingsControl && (
            <Grid item>
              <StudySettingsControl study={study} />
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default StudyDashboardHeader;
